import React, { useMemo, useState, useEffect } from 'react';
import { Grid, Typography, Box, Button, Alert, Select, MenuItem, FormControl } from '@mui/material';
import CalendarHeatmap from './CalendarHeatmap';
import { dailyOccupancyDataConst } from 'src/dailyOccupancyDataConst';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2 } from 'src/colors';
import { User } from 'src/interfaces/iuser';
import { LoadingCard } from 'src/App/HOC';
import { useSelector, useDispatch } from 'react-redux';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';
import { getDailyOccupancyRateCalendar } from 'src/store/Dashboard/actions';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import OccupancyLineGraph from 'src/App/Pages/OverviewPage/UtilityComponents/OccupancyLineGraph';
import { useCanAccessExpertPlan } from 'src/hooks/useFeatureAccess';

interface YearMonth {
  year: number;
  month: number;
  isPreliminary?: boolean;
}

interface Props {
  user: User;
  marketDisplayName: string;
  handleOnSampleClick: (plan: string) => void;
  isMobileScreen: boolean;
  isDemoMarket: boolean;
  warningTextComponent: any;
}

const DailyOccupancyRateCalendar: React.FC<Props> = ({
  user,
  marketDisplayName,
  handleOnSampleClick,
  isMobileScreen,
  isDemoMarket,
  warningTextComponent
}) => {
  const dispatch = useDispatch();
  const search = useSelector(state => (state as any).dashboard.search);
  const [isRevealed, setIsRevealed] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const dailyOccupancyRateCalendar = useSelector(
    state => (state as any).dashboard.dailyOccupancyRateCalendar
  );
  const upgradeMessage = useUpgradeMesageExpertFeature();
  const isNotPremiumUser = !useCanAccessExpertPlan();

  const {
    historicalData,
    prelimData,
    futureData,
    historicalStatus,
    prelimStatus,
    futureStatus,
    isRefreshAvailable,
  } = useMemo(() => {
    if (!isRevealed) {
      return {
        historicalData: dailyOccupancyDataConst,
        prelimData: {},
        futureData: Object.fromEntries(Object.entries(dailyOccupancyDataConst).slice(0, 90)),
        historicalStatus: 'success',
        prelimStatus: 'success',
        futureStatus: 'success',
        isRefreshAvailable: false,
      };
    }

    const historicalData = dailyOccupancyRateCalendar?.data?.historical_occupancy_calendar ?? {};
    const prelimData = dailyOccupancyRateCalendar?.data?.prelim_occupancy_calendar ?? {};
    const futureData = dailyOccupancyRateCalendar?.data?.future_occupancy_calendar ?? {};

    const historicalStatus =
      dailyOccupancyRateCalendar?.data?.historical_occupancy_calendar_status ?? 'processing';
    const prelimStatus =
      dailyOccupancyRateCalendar?.data?.prelim_occupancy_calendar_status ?? 'processing';
    const futureStatus =
      dailyOccupancyRateCalendar?.data?.future_occupancy_calendar_status ?? 'processing';

    const isRefreshAvailable = dailyOccupancyRateCalendar?.data?.is_refresh_available ?? false;

    // Transform the data to match the expected format
    const transformData = (data: any) => {
      return Object.entries(data).reduce((acc, [date, data]: [string, any]) => {
        acc[date] = data.occupancy;
        return acc;
      }, {} as Record<string, number>);
    };

    return {
      historicalData: transformData(historicalData),
      prelimData: transformData(prelimData),
      futureData: transformData(futureData),
      historicalStatus,
      prelimStatus,
      futureStatus,
      isRefreshAvailable,
    };
  }, [dailyOccupancyRateCalendar, isRevealed]);

  useEffect(() => {
    if (
      isRevealed &&
      (historicalStatus === 'processing' ||
        prelimStatus === 'processing' ||
        futureStatus === 'processing')
    ) {
      const interval = setInterval(() => {
        dispatch(getDailyOccupancyRateCalendar(search.data.id));
      }, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
    return () => {};
  }, [isRevealed, historicalStatus, prelimStatus, futureStatus, dispatch, search.data.id]);

  const handleReveal = () => {
    if (isNotPremiumUser) {
      handleOnSampleClick('expert');
      return;
    }
    setIsRevealed(true);
    dispatch(getDailyOccupancyRateCalendar(search.data.id));
  };

  const handleRefresh = () => {
    dispatch(getDailyOccupancyRateCalendar(search.data.id, true));
  };

  const uniqueYearMonths: YearMonth[] = useMemo(() => {
    const seen = new Set<string>();
    const result: YearMonth[] = [];

    // Process historical and preliminary data together
    const combinedData = { ...historicalData, ...prelimData };
    Object.keys(combinedData).forEach(dateStr => {
      const [yearStr, monthStr] = dateStr.split('-');
      const year = +yearStr;
      const month = +monthStr - 1;

      const key = `${year}-${month}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({
          year,
          month,
          isPreliminary: prelimData[dateStr] !== undefined,
        });
      }
    });

    result.sort((a, b) => (a.year === b.year ? a.month - b.month : a.year - b.year));

    return result;
  }, [historicalData, prelimData]);

  // Get available years from the data
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    uniqueYearMonths.forEach(({ year }) => years.add(year));
    return Array.from(years).sort((a, b) => b - a); // Sort in descending order
  }, [uniqueYearMonths]);

  // Set the most recent year as default when data changes
  useEffect(() => {
    if (availableYears.length > 0) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears]);

  // Filter year months based on selected year
  const filteredYearMonths = useMemo(() => {
    return uniqueYearMonths.filter(({ year }) => year === selectedYear);
  }, [uniqueYearMonths, selectedYear]);

  const futureYearMonths: YearMonth[] = useMemo(() => {
    const seen = new Set<string>();
    const result: YearMonth[] = [];

    Object.keys(futureData).forEach(dateStr => {
      const [yearStr, monthStr] = dateStr.split('-');
      const year = +yearStr;
      const month = +monthStr - 1;

      const key = `${year}-${month}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ year, month });
      }
    });

    result.sort((a, b) => (a.year === b.year ? a.month - b.month : a.year - b.year));

    return result;
  }, [futureData]);

  // Helper to transform occupancy data for the line graph
  const getLineGraphData = (
    data: Record<string, number>,
    year?: number,
    isPrelimMap?: Record<string, boolean>
  ) => {
    return Object.entries(data)
      .filter(([date]) => {
        if (!year) return true;
        return new Date(date).getFullYear() === year;
      })
      .map(([date, occupancy]) => ({
        date,
        occupancy,
        isPrelim: isPrelimMap ? isPrelimMap[date] : false,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  // For historical: merge historicalData and prelimData, and mark prelim days
  const isPrelimMap = Object.keys(prelimData).reduce((acc, date) => {
    acc[date] = true;
    return acc;
  }, {} as Record<string, boolean>);
  const historicalLineGraphData = getLineGraphData(
    { ...historicalData, ...prelimData },
    isRevealed ? selectedYear : undefined,
    isPrelimMap
  );
  const futureLineGraphData = getLineGraphData(futureData);

  const renderMonthTitle = (year: number, month: number, isPreliminary?: boolean) => {
    const currentMonthDate = new Date(year, month, 1);
    const monthName = currentMonthDate.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });

    if (isPreliminary) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" className="subHeadings">
            {monthName}
          </Typography>
          <TooltipNew
            title="Preliminary data"
            content="This month's occupancy is shown before full processing. Final rates are confirmed ~25 days after month-end using reviews to remove anomalies."
            arrow={true}
          >
            <WarningAmberIcon sx={{ color: '#FFA500', fontSize: 20 }} />
          </TooltipNew>
        </Box>
      );
    }

    return (
      <Typography variant="body1" className="subHeadings">
        {monthName}
      </Typography>
    );
  };

  const isBothProcessing = historicalStatus !== 'success' && prelimStatus !== 'success';

  return (
    <>
      <Grid
        container
        sx={{
          p: 2,
          boxShadow: '0 4px 10px #0003',
          borderRadius: 1,
          position: 'relative',
        }}
      >
        {isRevealed &&
          isRefreshAvailable &&
          historicalStatus !== 'processing' &&
          prelimStatus !== 'processing' && <RefreshButton handleRefresh={handleRefresh} />}

        {warningTextComponent && warningTextComponent}

        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="body1" className="subHeadings">
            Historical Daily Occupancy Calendar of {marketDisplayName}
          </Typography>
          {isRevealed && availableYears.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value as number)}
                sx={{ height: 36 }}
              >
                {availableYears.map(year => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>

        <Grid
          container
          justifyContent="center"
          alignItems="flex-start"
          flexWrap="wrap"
          sx={{
            gap: 4,
            mt: 2,
            filter: !isRevealed ? 'blur(4px) brightness(1.1)' : 'none',
            pointerEvents: !isRevealed ? 'none' : 'auto',
          }}
        >
          {isRevealed && isBothProcessing && (
            <LoadingCard height={150} type="bars" noShadow={true} />
          )}

          {(historicalStatus === 'success' || prelimStatus === 'success' || !isRevealed) &&
            (isRevealed ? filteredYearMonths : uniqueYearMonths).map(
              ({ year, month, isPreliminary }) => (
                <Grid item key={`${year}-${month}`}>
                  <CalendarHeatmap
                    year={year}
                    month={month}
                    occupancyData={isPreliminary ? prelimData : historicalData}
                    monthTitle={renderMonthTitle(year, month, isPreliminary)}
                    secondaryData={futureData}
                    calendarType="historical"
                  />
                </Grid>
              )
            )}

          {(historicalStatus !== 'success' || prelimStatus !== 'success') && isRevealed && (
            <Box
              sx={{
                position: isBothProcessing ? 'absolute' : 'relative',
                top: isBothProcessing ? '50%' : '0',
                left: isBothProcessing ? '50%' : '0',
                transform: isBothProcessing ? 'translate(-50%, -50%)' : 'none',
                zIndex: 1,
              }}
            >
              <Alert severity="info">
                {isBothProcessing || historicalStatus !== 'success'
                  ? 'Processing historical occupancy data...'
                  : 'Processing more recent historical occupancy data...'}
              </Alert>
            </Box>
          )}
        </Grid>

        {(historicalStatus === 'success' || prelimStatus === 'success' || !isRevealed) &&
          !(isRevealed && isBothProcessing) && (
            <Box
              sx={{
                mt: 4,
                width: '100%',
                filter: !isRevealed ? 'blur(4px) brightness(1.1)' : 'none',
              }}
            >
              <OccupancyLineGraph data={historicalLineGraphData} />
            </Box>
          )}

        {!isRevealed && (
          <ActionButton
            isNotPremiumUser={isNotPremiumUser}
            isMobileScreen={isMobileScreen}
            handleReveal={handleReveal}
            upgradeMessage={upgradeMessage}
          />
        )}
      </Grid>

      <Grid
        container
        sx={{
          p: 2,
          boxShadow: '0 4px 10px #0003',
          borderRadius: 1,
          position: 'relative',
          mt: 4,
        }}
      >
        {isRevealed && isRefreshAvailable && futureStatus !== 'processing' && (
          <RefreshButton handleRefresh={handleRefresh} />
        )}

        <Typography variant="body1" className="subHeadings">
          Future 90 Days Daily Occupancy Calendar of {marketDisplayName}
        </Typography>

        <Grid
          container
          justifyContent="center"
          alignItems="flex-start"
          flexWrap="wrap"
          sx={{
            gap: 4,
            mt: 2,
            filter: !isRevealed ? 'blur(4px) brightness(1.1)' : 'none',
            pointerEvents: !isRevealed ? 'none' : 'auto',
          }}
        >
          {isRevealed && futureStatus !== 'success' && (
            <LoadingCard height={150} type="bars" noShadow={true} />
          )}

          {futureStatus !== 'success' && isRevealed && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
              }}
            >
              <Alert severity="info">Processing future occupancy data...</Alert>
            </Box>
          )}

          {(futureStatus === 'success' || !isRevealed) &&
            futureYearMonths.map(({ year, month }) => (
              <Grid item key={`${year}-${month}`}>
                <CalendarHeatmap
                  year={year}
                  month={month}
                  occupancyData={futureData}
                  monthTitle={renderMonthTitle(year, month)}
                  secondaryData={{ ...historicalData, ...prelimData }}
                  calendarType="future"
                />
              </Grid>
            ))}
        </Grid>

        {(futureStatus === 'success' || !isRevealed) &&
          !(isRevealed && futureStatus !== 'success') && (
            <Box
              sx={{
                mt: 4,
                width: '100%',
                filter: !isRevealed ? 'blur(4px) brightness(1.1)' : 'none',
              }}
            >
              <OccupancyLineGraph data={futureLineGraphData} />
            </Box>
          )}

        {!isRevealed && (
          <ActionButton
            isNotPremiumUser={isNotPremiumUser}
            isMobileScreen={isMobileScreen}
            handleReveal={handleReveal}
            upgradeMessage={upgradeMessage}
          />
        )}
      </Grid>
    </>
  );
};

const ActionButton = ({ isNotPremiumUser, isMobileScreen, handleReveal, upgradeMessage }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
        textAlign: 'center',
      }}
    >
      {isNotPremiumUser ? (
        <Typography
          variant="body1"
          sx={{
            fontSize: isMobileScreen ? 18 : 22,
            fontWeight: 600,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            textDecoration: 'underline',
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: '#fffe',
          }}
          onClick={handleReveal}
        >
          {upgradeMessage}
        </Typography>
      ) : (
        <Button
          variant="contained"
          onClick={handleReveal}
          sx={{
            backgroundColor: COLOR_PRIMARY_2,
            color: '#fff',
            boxShadow: '0 4px 10px #0003',
            fontWeight: 600,
            fontSize: isMobileScreen ? 18 : 22,
            px: 2,
            py: 1,
            transition: 'transform 0.15s, box-shadow 0.15s',
            '&:hover': {
              backgroundColor: COLOR_PRIMARY_2,
              opacity: 0.9,
              color: '#fff',
            },
          }}
        >
          Reveal Data
        </Button>
      )}
    </Box>
  );
};

const RefreshButton = ({ handleRefresh }) => {
  return (
    <Tooltip title="Refresh to get the latest data">
      <IconButton
        onClick={handleRefresh}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 3,
          background: '#fff',
          boxShadow: '0 2px 8px #0002',
          '&:hover': { background: '#f5f5f5' },
        }}
        size="small"
      >
        <RefreshIcon color="primary" />
      </IconButton>
    </Tooltip>
  );
};
export default React.memo(DailyOccupancyRateCalendar);
